<template>
  <div>
    <div v-if="loading">loading...</div>
    <form
      v-else
      class="w-full flex flex-col gap-y-4 text-sm"
      novalidate
      @submit.prevent="onSubmit"
      @invalid-submit.prevent="onInvalidSubmit"
    >
      <template :key="key" v-for="(section, key) in accordionList">
        <CollapsibleSection
          v-if="section.isShowAccodion"
          :key="key"
          :header-label="$t(section.header)"
          :body-class="useLayoutClasses(section?.form_style?.web?.layout)?.['bodyClass']"
        >
          <template :key="field.field_name" v-for="field in section.schema">
            <component
              v-if="canShow(field.showRules, field.device_type)"
              :is="findComponent(field.component)"
              :field-name="field.field_name"
              :dependent-on="field.dependent_on"
              :on-change="field.on_change"
              :on-blur="field.on_blur"
              :on-watch="field.on_watch"
              :watchAction="field.watchAction"
              :default-value="formatDefaultValue(field)"
              :is-disabled="field.disabled"
              :server-date-format="field.server_date_format"
              :min-date="field.min_date"
              :max-date="field.max_date"
              :prefixDisabled="field.prefixDisabled"
              :showRules="field.showRules"
              :radioOptions="field.radio_options"
              :sequenceOptions="field.sequence_options"
              :templateSchema="field.template_schema"
              :editFieldNamePrefix="field.edit_field_name_prefix"
              :addressType="field.address_type"
              :resetValues="field.reset_values"
              :visibleColumns="field.visible_columns"
              :layout="section?.form_style?.web?.layout"
              :read-only="field.read_only"
              :fullWidth="field.full_width"
              :trueValue="field.true_value"
              :falseValue="field.false_value"
              :show-password="field.input_type == 'password'"
              :popoverContent="field.popover_content"
              v-bind="{ ...field, ...useLayoutClasses(section?.form_style?.web?.layout) }"
              @keydown.enter.prevent=""
            ></component>
            <!-- @keydown.enter.stop.prevent="" to prevent {enter} from toggling the section -->
          </template>
        </CollapsibleSection>
      </template>
      <div class="flex justify-between">
        <div v-if="formStore.activeStepIndex === 0" class="size-8"></div>
        <FilledButton
          v-else
          :disabled="formStore.activeStepIndex === 0"
          class="px-5 py-1.5 text-white"
          @click.prevent="$emit('onBack')"
        >
          {{ $t('back') }}
        </FilledButton>

        <FilledButton class="px-5 py-1.5 text-white" @click.prevent="onSubmit">
          {{ $t('next') }}
        </FilledButton>
      </div>
    </form>
    <!-- for debugging purposes -->
    <!-- <Teleport to="body">
      <button
        class="fixed end-12 bottom-12 size-12 bg-teal-100 rounded-full shadow-xl flex items-center justify-center hover:scale-105 hover:bg-teal-200"
        @click.stop.prevent="useDebugFill($route.params.serviceId)"
      >
        <faCog class="w-4 hover:animate-spin" />
      </button>
    </Teleport> -->
  </div>
</template>

<script setup>
import FilledButton from '@/components/Buttons/Filled.vue'
import AddressFormGroup from '@/components/Form/AddressFormGroup.vue'
import AttachmentsWrapper from '@/components/Form/AttachmentsWrapper.vue'
import CheckboxTicker from '@/components/Form/CheckboxTicker.vue'
import ComboText from '@/components/Form/ComboText.vue'
import CommonButton from '@/components/Form/CommonButton.vue'
import DataTable from '@/components/Form/DataTable.vue'
import DateTimePicker from '@/components/Form/DateTimePicker.vue'
import DatepickerCalendar from '@/components/Form/DatepickerCalendar.vue'
import DropdownSearch from '@/components/Form/DropdownSearch.vue'
import AutocompleteDropdown from '@/components/Form/AutocompleteDropdown.vue'
import InfoBar from '@/components/Form/InfoBar.vue'
import InfoLabel from '@/components/Form/InfoLabel.vue'
import InfoMessage from '@/components/Form/InfoMessage.vue'
import MobileButtonList from '@/components/Form/MobileButtonList.vue'
import MultipleDropdownSearch from '@/components/Form/MultipleDropdownSearch.vue'
import OcrBanner from '@/components/Form/OcrBanner/index.vue'
import PopupTrigger from '@/components/Form/PopupTrigger.vue'
import radio_group from '@/components/Form/RadioGroup.vue'
import ReactiveDropdown from '@/components/Form/ReactiveDropdown.vue'
import ReactiveFormGroup from '@/components/Form/ReactiveFormGroup.vue'
import RecaptchaTicker from '@/components/Form/RecaptchaTicker.vue'
import RefundAmountGroup from '@/components/Form/RefundAmountGroup.vue'
import SpacerComponent from '@/components/Form/SpacerVue.vue'
import TextInput from '@/components/Form/TextInput.vue'
import TextRenderer from '@/components/Form/TextRenderer.vue'
import TimePicker from '@/components/Form/TimePicker.vue'
import TwowayDropdown from '@/components/Form/TwowayDropdown.vue'
import VisaFileNumber from '@/components/Form/VisaFileNumber.vue'
import DraftsTable from '@/components/Form/GroupDrafts/index.vue'
import CollapsibleSection from '@/components/Ui/CollapsibleSection.vue'
import useDraftPayloadOverride from '@/composables/draft_payload_override/base.js'
import usePreloadMapping from '@/composables/preLoad/usePreloadMapping'
import usePreSubmit from '@/composables/preSubmit/index'
import useVisible from '@/composables/useVisible'
import { useFormStore } from '@/stores/form'
import { useGlobalStore } from '@/stores/global'
import lodashGet from 'lodash/get'
import { DateTime } from 'luxon'
import { notify } from 'notiwind'
import { useForm } from 'vee-validate'
import { onMounted, ref, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import useLayoutClasses from '@/composables/useLayoutClasses'
import DownloadLink from '@/components/Form/DownloadLink.vue'
import RecaptchaTickerV2 from '@/components/Form/RecaptchaTickerV2.vue'

const emit = defineEmits('onNext')

const route = useRoute()
const globalStore = useGlobalStore()
const i18n = useI18n()
const { findShowRule, getBindObj } = useVisible()

globalStore.languageId = i18n.locale.value == 'en' ? 2 : 1

const formStore = useFormStore()

const serviceId = +route.params.serviceId
const loading = ref(true)
const accordionList = ref([])

const loadDraft = async () => {
  try {
    const requestNumber = route.query.requestNumber
    if (!requestNumber || formStore.activeStepIndex !== 0) return
    await formStore.loadDraftFromRquestNumber(requestNumber)
    await formStore.getAttachmentFromDraft()
  } catch (error) {
    console.error('Error in loadDraft: ', error)
  }
}

onMounted(async () => {
  try {
    await performModuleSpecificLogic()
    await globalStore.startService(serviceId)
  } catch (error) {
    console.error('Error in onMounted: ', error)
  }
  formatAccordionList()
  loading.value = false
})

formStore.form = useForm({
  initialValues: formStore.draft?.request || {}
})

const formatDefaultValue = (field) => {
  return (
    lodashGet(formStore.draft?.request || {}, field.field_name) ||
    lodashGet(formStore.form.values || {}, field.field_name) ||
    lodashGet(formStore.form || {}, field.field_name) ||
    field.default_value
  )
}

const checkRecaptcha = () => {
  if (
    formStore?.activeForm?.config?.web?.isNeedRecaptcha &&
    !formStore.extraMap.recaptchaResponse
  ) {
    notify(
      {
        group: 'error',
        title: 'error',
        text: i18n.t('captchaNotProcessed')
      },
      6000
    )
    return false
  } else {
    return true
  }
}

const processServerRequiredDateFormate = (payload) => {
  const transformDate = (item) => {
    if (payload.request[item.field_name]) {
      const isDateValid = DateTime.fromFormat(
        payload.request[item.field_name],
        'dd/MM/yyyy'
      ).isValid
      const res = DateTime.fromFormat(payload.request[item.field_name], 'dd/MM/yyyy')
        .toFormat(item.server_date_format)
        .toString()
      if (isDateValid) {
        payload.request[item.field_name] = res
      }
    }
  }
  formStore?.activeForm?.form?.map((accordion) => {
    accordion.schema
      .filter((item) => item.device_type != 'mobile')
      .map((item) => {
        if (item.field_name && item.server_date_format) {
          transformDate(item)
        } else if (item['template_schema']?.length > 0 || item['component_list']?.length > 0) {
          item['component_list']?.map((component) => {
            if (
              component.field_name &&
              component.server_date_format &&
              payload.request[component.field_name]
            ) {
              transformDate(component)
            }
          })
          // Formate correct date for AddressFormGroup and ReactiveFormGroup
          item['template_schema']?.map((component) => {
            if (component.field_name && component.server_date_format) {
              if (payload.request[item.field_name]?.length > 0) {
                // vee-validate does not allow direct value setting, so we need to copy and process it.
                payload.request[item.field_name] = [...payload.request[item.field_name]]
                payload.request[item.field_name] = payload.request[item.field_name].map(
                  (currentItem) => {
                    // vee-validate does not allow direct value setting, so we need to copy and process it.
                    const newCurrentItem = { ...currentItem }

                    if (newCurrentItem[component.field_name]) {
                      const res = DateTime.fromFormat(
                        newCurrentItem[component.field_name],
                        'dd/MM/yyyy'
                      )
                        .toFormat(component.server_date_format)
                        .toString()
                      newCurrentItem[component.field_name] = res
                    }
                    return newCurrentItem
                  }
                )
              }
            }
          })
        }
      })
  })

  return payload
}

const removeIgnoredFields = (payload) => {
  const { ignore_fields = [] } = formStore.schema.api

  // If ignore_fields is not an array or is empty, return the payload unchanged
  if (!Array.isArray(ignore_fields) || ignore_fields.length === 0) return payload

  return {
    ...payload,
    request: Object.keys(payload.request).reduce((acc, key) => {
      if (!ignore_fields.includes(key)) {
        acc[key] = payload.request[key]
      }
      return acc
    }, {})
  }
}

const sortPayloadKeys = (payload) => {
  const { request } = payload
  const keys = Object.keys(request).sort((a, b) => a > b)
  return {
    ...payload,
    request: keys.reduce((acc, key) => {
      acc[key] = request[key]
      return acc
    }, {})
  }
}

const replaceMask = (payload) => {
  const { request } = payload
  Object.entries(request).forEach(([key, value]) => {
    if (value && typeof value == 'string' && value.indexOf('784-') >= 0) {
      request[key] = value.replace(/-/g, '')
    }
  })
  return request
}

const onSubmit = async () => {
  formStore.form.setTouched(true)
  const validationResult = await formStore.form.validate()
  if (!validationResult.valid && !globalStore.showcase.skipValidation) {
    const messages = Object.entries(formStore.form.errors)
      // Ignore nested form fields validation
      .filter(([key]) => key.indexOf('edit_') < 0)
      .map(([key, value]) => `${i18n.t(key)}: ${value}`)
      .join('<br/>')
    notify(
      {
        group: 'error',
        title: 'error',
        text: messages
      },
      6000
    )
    if (messages?.length > 0) {
      return
    }
  }

  await formStore.uploadAttachments()
  await formStore.cleanupDeletedAttachments()

  const payload = {
    // Due to vee-validate not allowing direct value setting, it requires copying and subsequent processing.
    request: { ...formStore.draft.request, ...formStore.form.values },
    serviceTransactionId: route.params.serviceId || globalStore.startedService.serviceTransactionId
  }
  Object.assign(payload, await useDraftPayloadOverride(route.params.serviceId))
  Object.assign(payload, removeIgnoredFields(payload))
  Object.assign(payload, processServerRequiredDateFormate(payload))
  Object.assign(payload, sortPayloadKeys(payload))
  // Replace mask for 784-XXXX-XXXX
  Object.assign(payload, { request: replaceMask(payload) })

  if (formStore.extraMap.recaptchaResponse) {
    payload.request.recaptchaResponse = formStore.extraMap.recaptchaResponse
  }
  if (!checkRecaptcha()) return
  if (!(await usePreSubmit(route.params.serviceId, payload))) return
  try {
    if (!formStore?.draft?.requestNumber) await formStore.submitDraft(payload)
    else {
      // Object.assign(payload, { ...formStore.draft })
      Object.assign(formStore.draft, { ...payload })
      await formStore.updateDraft(formStore.draft)
    }
  } catch (error) {
    if (globalStore.showcase.skipValidation) {
      emit('onNext')
    }
    return
  }
  emit('onNext')
}

const findComponent = (component) => {
  switch (component) {
    case 'ComboText':
      return ComboText
    case 'AutocompleteDropdown':
      return AutocompleteDropdown
    case 'DropdownSearch':
      return DropdownSearch
    case 'ReactiveFormGroup':
    case 'reactive_form_group':
      return ReactiveFormGroup
    case 'AddressFormGroup':
    case 'address_form_group':
      return AddressFormGroup
    case 'MultipleDropdownSearch':
    case 'multiple_dropdown_search':
      return MultipleDropdownSearch
    case 'InfoBar':
      return InfoBar
    case 'InfoMessage':
      return InfoMessage
    case 'TextInput':
      return TextInput
    case 'TwowayDropdown':
      return TwowayDropdown
    case 'DatepickerCalendar':
      return DatepickerCalendar
    case 'TimePicker':
      return TimePicker
    case 'DateTimePicker':
      return DateTimePicker
    case 'ReactiveDropdown':
      return ReactiveDropdown
    case 'RecaptchaTicker':
      return RecaptchaTickerV2
    case 'InfoLabel':
      return InfoLabel
    case 'RadioGroup':
    case 'radio_group':
      return radio_group
    case 'VisaFileNumber':
      return VisaFileNumber
    case 'CommonButton':
      return CommonButton
    case 'MobileButtonList':
      return MobileButtonList
    case 'Spacer':
      return SpacerComponent
    case 'TextRender':
      return TextRenderer
    case 'CheckBox':
      return CheckboxTicker
    case 'OcrBanner':
      return OcrBanner
    case 'RefundAmountGroup':
      return RefundAmountGroup
    case 'DataTable':
      return DataTable
    case 'PopupTrigger':
      return PopupTrigger
    case 'AttachmentsUploader':
      return AttachmentsWrapper
    case 'DraftsTable':
      return DraftsTable
    case 'DownloadLink':
      return DownloadLink
    default:
      return component
  }
}

/**
 * here's where module specific logic should be done
 */
const performModuleSpecificLogic = async () => {
  await loadDraft()
  formStore.initializeDraft()
  const preloadFunc = await usePreloadMapping(+route.params.serviceId)
  await preloadFunc?.()
}

const canShow = (showRules, deviceType) => {
  if (deviceType === 'mobile') return false
  if (!showRules) return true

  return findShowRule(
    showRules.any || showRules.all,
    showRules.any ? 'any' : 'all',
    getBindObj(route.params.serviceId)
  )
}

const formatAccordionList = () => {
  accordionList.value = formStore.activeForm?.form
    .filter((f) => f.device_type != 'mobile')
    .map((accordion) => {
      return {
        ...accordion,
        schema: accordion.schema.filter((s) => s.device_type != 'mobile'),
        isShowAccodion: accordion.showRules
          ? findShowRule(
              accordion.showRules.all || accordion.showRules.any,
              accordion.showRules.all ? 'all' : 'any',
              getBindObj(route.params.serviceId)
            )
          : true
      }
    })
}

watchEffect(() => {
  getBindObj(route.params.serviceId)
  // prevent first time when preload not finished
  if (!loading.value) {
    formatAccordionList()
  }
})

watch(i18n.locale, async () => {
  sessionStorage.clear()
  window.location.reload()
})
</script>

<style lang="scss" scoped></style>
